<template>
  <div class="studio-product-checkout-stripe-confirm container">
    <div class="row">
      <div class="col-12">
        <!--        <h1 class="my-4">{{ $t('PRODUCTS.TITLE.CHECKOUT') }}</h1>-->
      </div>
    </div>
    <div
      class="row d-flex justify-content-center my-5"
      v-if="!waitingForPurchase && product"
    >
      <div class="col-12 content">
        <ProductPostPurchaseModal
          class="justify-content-start"
          :product="product"
          :studioURL="studioURL"
          :hideHeader="true"
          :viewRouteName="getViewRouteName"
          :viewButtonText="getReturnButtonText"
          :eventId="eventId"
        />
      </div>
    </div>
    <div
      class="row d-flex justify-content-center my-5"
      v-else-if="paymentError"
    >
      <div class="col-12 text-center">
        <router-link
          :to="{
            name: 'studio-product-checkout',
            params: { product_id: productId },
          }"
        >
          {{ $t('PRODUCTS.TEXT.PAYMENT_ERROR_RETURN_TO_CHECKOUT') }}
        </router-link>
      </div>
    </div>
    <div class="row d-flex justify-content-center my-5" v-else>
      <div class="col-12 text-center">
        <h2>{{ $t('PRODUCTS.TEXT.WAITING_FOR_PAYMENT_CONFIRMATION') }}</h2>
        <HMSpinner class="mt-4" />
        <div class="py-5"></div>
        <div class="py-5"></div>
        <div class="py-5"></div>
        <div class="py-5"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ProductPostPurchaseModal from '@/web/components/products/ProductPostPurchaseModal';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import Cookies from 'js-cookie';
import StripePaymentModal from '../../components/products/StripePaymentModal';
import { trackFBPixelStudioPurchase } from '@/web/services/analytics';
import ProductUpsellModal from '../../components/products/ProductUpsellModal';

export default {
  name: 'StudioProductCheckoutStripeConfirm',
  components: {
    ProductPostPurchaseModal,
  },
  computed: {
    ...mapGetters({
      product: 'product/product',
      currency: 'studio/currency',
      isLoggedIn: 'user/isLoggedIn',
      user: 'user/user',
      studio: 'studio/currentStudio',
    }),
    productId() {
      return this.$route.params.product_id;
    },
    studioURL() {
      return getStudioURL(this.$route);
    },
    getViewRouteName() {
      return this.eventId ? 'studio-event-details' : 'studio-product';
    },
    getReturnButtonText() {
      return this.eventId
        ? this.$t('PRODUCTS.BUTTON.RETURN_TO_EVENT')
        : this.$t('PRODUCTS.BUTTON.VIEW_PRODUCT');
    },
    stripeCustomerData() {
      return this.purchasedProduct?.existing_stripe_customer;
    },
    existingCreditCard() {
      return this.stripeCustomerData?.invoice_settings?.default_payment_method
        ?.card;
    },
  },
  data() {
    return {
      purchasedProduct: null,
      waitingForPurchase: true,
      eventId: null,
      stripe: null,
      paymentError: false,
    };
  },
  methods: {
    async loadProductStatus() {
      this.purchasedProduct = await this.$store.dispatch(
        'product/getProductForPurchase',
        {
          studioURL: this.studioURL,
          productId: this.productId,
        }
      );
      if (!this.purchasedProduct.purchased) {
        this.waitingForPurchase = true;
        setTimeout(() => {
          this.loadProductStatus();
        }, 5000);
      } else {
        this.$store.dispatch('user/studentPunches');
        this.waitingForPurchase = false;
        if (this.existingCreditCard && this.purchasedProduct.upsell_available) {
          if (
            this.purchasedProduct.upsell_product &&
            this.purchasedProduct.upsell_seconds_remaining > 0
          ) {
            this.$modal.show(
              ProductUpsellModal,
              {
                product: this.purchasedProduct,
                studioURL: this.studioURL,
              },
              {
                clickToClose: false,
              }
            );
          }
        }
      }
    },
    initStripe() {
      this.stripe = window.Stripe(this.purchasedProduct.stripe_publishable_key);
    },
    openPaymentModal(setupIntent, paymentIntent) {
      this.$modal.show(
        StripePaymentModal,
        {
          user: this.user,
          product: this.product,
          setupIntent: setupIntent ?? { id: null },
          paymentIntent: paymentIntent ?? { id: null },
          studioURL: this.studioURL,
          stripePublishableKey: this.purchasedProduct.stripe_publishable_key,
          buttonText: this.$t('PRODUCTS.BUTTON.TRY_AGAIN'),
          onClose: () => {
            this.inProgress = false;
          },
        },
        {
          clickToClose: false,
        }
      );
    },
    processFBPixelCookie() {
      const fbPixelCookie = Cookies.get('fbPurchasePayload');
      if (!fbPixelCookie) return;
      const fbPixelPayload = JSON.parse(fbPixelCookie);
      console.log('fbPixelPayload', fbPixelPayload);
      if (!fbPixelPayload) return;
      trackFBPixelStudioPurchase(
        fbPixelPayload.fb_pixel_id,
        fbPixelPayload.payload
      );
      Cookies.remove('fbPurchasePayload');
    },
    async checkStripeStatus() {
      const setupIntentClientSecret = this.$route.query
        .setup_intent_client_secret;
      const paymentIntentClientSecret = this.$route.query
        .payment_intent_client_secret;
      if (setupIntentClientSecret) {
        const response = await this.stripe.retrieveSetupIntent(
          setupIntentClientSecret
        );
        console.log('retrieveSetupIntent=', response);
        if (response.error) {
          this.paymentError = true;
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Payment failed',
            text: response.error.message,
            duration: -1, // stay ON
          });
        } else {
          console.log(
            'response.setupIntent.status',
            response.setupIntent.status
          );
          if (response.setupIntent.status === 'succeeded') {
            try {
              await this.$store.dispatch('product/verifyIntent', {
                studioURL: this.studioURL,
                productId: this.productId,
                setupIntentId: response.setupIntent.id,
              });
              // eslint-disable-next-line no-empty
            } catch {}
            this.processFBPixelCookie();
            this.$store.dispatch('user/studentPunches');
            this.$store.dispatch('studio/getStudio', this.studioURL);
          } else if (
            ['requires_payment_method', 'requires_source'].find(
              e => e === response.paymentIntent.status
            )
          ) {
            // payment failed....
            this.$notify({
              group: 'app',
              type: 'error',
              title: 'Payment failed',
              text:
                'You can try entering your payment details again or return to checkout.',
              duration: -1, // stay ON
            });
            this.openPaymentModal(response.setupIntent, null);
          } else {
            setTimeout(() => {
              this.checkStripeStatus();
            }, 5000);
          }
        }
      }
      if (paymentIntentClientSecret) {
        const response = await this.stripe.retrievePaymentIntent(
          paymentIntentClientSecret
        );
        console.log('retrievePaymentIntent=', response);
        if (response.error) {
          this.paymentError = true;
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Payment failed',
            text: response.error.message,
            duration: -1, // stay ON
          });
        } else {
          console.log(
            'response.paymentIntent.status',
            response.paymentIntent.status
          );
          if (response.paymentIntent.status === 'succeeded') {
            try {
              await this.$store.dispatch('product/verifyIntent', {
                studioURL: this.studioURL,
                productId: this.productId,
                paymentIntentId: response.paymentIntent.id,
              });
              // eslint-disable-next-line no-empty
            } catch {}
            this.processFBPixelCookie();
            this.$store.dispatch('user/studentPunches');
            this.$store.dispatch('studio/getStudio', this.studioURL);
          } else if (
            ['requires_payment_method', 'requires_source'].find(
              e => e === response.paymentIntent.status
            )
          ) {
            // payment failed....
            this.$notify({
              group: 'app',
              type: 'error',
              title: 'Payment failed',
              text:
                'You can try entering your payment details again or return to checkout.',
              duration: -1, // stay ON
            });
            this.openPaymentModal(null, response.paymentIntent);
          } else {
            setTimeout(() => {
              this.checkStripeStatus();
            }, 5000);
          }
        }
      }
    },
  },
  async mounted() {
    this.eventId = Cookies.get('checkouteventid')
      ? parseInt(Cookies.get('checkouteventid'))
      : null;
    this.$store.dispatch('product/getProductDetail', {
      studioURL: this.studioURL,
      productId: this.productId,
    });
    await this.loadProductStatus();
    this.initStripe();
    await this.checkStripeStatus();
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.content {
  max-width: 600px;
}
</style>
